import React, { useState } from 'react';
import { FlatList, Image, Linking } from "react-native";
import type { FocusPositionProps, MyPlayerProps, WalletSettingsProps } from '../../types';
import { Checkbox, Icons } from '../../Components';
import { WalletApi } from '../api';
import moment from 'moment-mini';
import { useColors } from '../../constants/useColors';
import { Button, Text, TextInput, View } from '../../Components/Themed';

type WalletSetupProps = {
    player: MyPlayerProps,
    wallet_settings: WalletSettingsProps,
    onSettingsUpdate:(ws:WalletSettingsProps) => void,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    onClose?:() => void,
    onNext?:() => void
}
const setup_sections = ['header','auth','terms','set_pin', 'show_next']
const WalletSetup = ({ wallet_settings, onSettingsUpdate, onFocusPosition, onClose, onNext }:WalletSetupProps) => {
    const Colors = useColors();
    const [ terms_agreed, setTermsAgreed ] = useState(false);
    const [ auth_error, setAuthError ] = useState<string>();
    const [ pin_view, setPinView ] = useState<'set'|'confirm'>('set');
    const [ pin_settings, setPinSettings ] = useState({
        pin: '',
        confirm_pin:''
    })
    const { pin, confirm_pin } = pin_settings;

    const pin_valid = pin.length >= 4 ? true:false;
    const go_valid = pin_valid && confirm_pin == pin ? true : false;

    const handleAgreeTerms = async() => {
        const ws = await WalletApi.agreeToTerms()
        onSettingsUpdate(ws)
    }

    const handleSetPin = async() => {
        if(!pin_valid || !go_valid){ return }
        const ws = await WalletApi.setPin(pin)
        onSettingsUpdate(ws);
    }

    const handleAuthPin = async() => {
        if(!pin_valid){ return }
        try {
            const ws = await WalletApi.authenticatePin(pin);
            onSettingsUpdate(ws);
        } catch (e) {
            setAuthError('Invalid Pin - Please try again')
        }
    }

    const auth_required = wallet_settings.auth_end_datetime && moment().isAfter(wallet_settings.auth_end_datetime) ? true : false

    const renderSections = (data:{item:string,index:number}) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ padding:20 }}>
                        <Text theme='h1'>MY WALLET</Text>
                        {onNext ?
                        <Text style={{ marginTop:5 }} theme='description'>Welcome to your wallet.  Go through the wizard to get set up to deposit and withdraw!</Text>
                        :!wallet_settings.agreed_to_terms ?
                        <Text style={{ marginTop:5 }} theme='description'>Welcome to your wallet.  This is where your sensitive account details are kept.  Please review our terms and conditions!</Text>
                        :
                        <Text style={{ marginTop:5 }} theme='description'>Welcome to your wallet.  Please enter your security pin to continue!</Text>
                        }
                    </View>
                )
            case 'terms':
                if(wallet_settings.agreed_to_terms){ return <></> }
                return (
                <View style={{ flex:1, padding:20 }}>
                    <View>
                        <Text size={20} theme='h2' textAlign='center'>BettorEdge Wallet</Text>
                        <Text style={{ marginTop:5 }} size={26} theme='h1' textAlign='center'>Terms and Conditions</Text>
                    </View>
                    <Button onPress={() => Linking.openURL('https://www.bettoredge.com/wallet-terms-of-use')}>
                        <Text size={14} theme='description' textAlign='center'>Press here to review our <Text size={14} theme='action'>Wallet Terms of Use</Text>. To continue setting up your wallet please review and agree to the terms.</Text>
                    </Button>
                    <Button 
                        style={{ marginTop:15, flexDirection:'row', alignItems:'center', padding:20, borderRadius:8 }}
                        onPress={() => setTermsAgreed(!terms_agreed)}>
                        <Text style={{ flex:1 }} size={16} theme='description'>I have read and agree to the terms</Text>
                        <Checkbox size={30} checked={terms_agreed} disabled onSelect={() => console.log('')}/>
                    </Button>
                    <View style={{ justifyContent:'center', alignItems:'center' }}>
                    <Image
                        source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1717098691/Signing_the_contract_stfvtj.png' }}
                        style={{ width: 250, height:250 }}
                        resizeMode='cover'
                    />
                    
                    </View>
                </View>
                )
            case 'set_pin':
                if(wallet_settings.pin || !wallet_settings.agreed_to_terms){ return <></> }
                return (
                <View style={{ flex:1, padding:20 }}>
                    <View>
                        <Text style={{ marginTop:5, marginBottom:10 }} size={26} theme='h1' textAlign='center'>{pin_view == 'set' ? 'Set' : 'Confirm'} Security PIN</Text>
                        {pin_view == 'set' ?
                            <TextInput
                                style={{ width:200, alignSelf:'center', textAlign:'center' }}
                                placeholder='HARDPIN'
                                onFocusPosition={onFocusPosition}
                                value={pin}
                                onChangeText={(text) => setPinSettings({ ...pin_settings, pin: text })}
                            />
                            :
                            <View>
                                <TextInput
                                    style={{ width:200, alignSelf:'center', textAlign:'center' }}
                                    placeholder='RE-Enter PIN'
                                    onFocusPosition={onFocusPosition}
                                    value={confirm_pin}
                                    onChangeText={(text) => setPinSettings({ ...pin_settings, confirm_pin: text })}
                                />
                            </View>
                            }
                    </View>
                    <View style={{ margin:15 }}>
                        <Text size={14} theme='description' textAlign='center'>Let's secure your sensitive information. Before doing any activity in your wallet, you will be required to enter a pin.  Lets set it now.</Text>
                    </View>
                    <View style={{ justifyContent:'center', alignItems:'center' }}>
                        <Image
                            source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1717099686/Secure_lock_and_key_successfully_unlocked_it6hy0.png' }}
                            style={{ width: 250, height:250 }}
                            resizeMode='cover'
                        />
                    </View>
                </View>
                )
            case 'auth':
                if(!auth_required || !wallet_settings.pin || !wallet_settings.agreed_to_terms){ return <></> }
                return (
                <View style={{ flex:1, padding:20, paddingBottom:400 }}>
                    <View>
                        <Text size={20} theme='h2' textAlign='center'>BettorEdge Wallet</Text>
                        <Text style={{ marginTop:5 }} size={26} theme='h1' textAlign='center'>Enter Security PIN</Text>
                    </View>
                    <View style={{ justifyContent:'center', alignItems:'center' }}>
                        <Image
                            source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1717099686/Secure_lock_and_key_successfully_unlocked_it6hy0.png' }}
                            style={{ width: 250, height:250 }}
                            resizeMode='cover'
                        />
                    </View>
                    <View style={{ marginBottom:15 }}>
                        <Text size={14} theme='description'>Before you can continue to your wallet.  Please enter your security pin.</Text>
                    </View>
                    <TextInput
                        style={{ width:200, alignSelf:'center' }}
                        placeholder='ENTER PIN'
                        onFocusPosition={onFocusPosition}
                        value={pin}
                        onChangeText={(text) => setPinSettings({ ...pin_settings, pin: text })}
                    />
                    {auth_error ? 
                    <View style={{ padding:10 }}>
                        <Text size={14} color={Colors.text.warning} weight='bold' textAlign='center'>{auth_error}</Text>
                    </View>
                    :<></>}
                </View>
                )
            case 'show_next':
                if(!wallet_settings.pin){ return <></> }
                if(!wallet_settings.agreed_to_terms){ return <></> }
                if(auth_required){ return <></> }
                return (
                    <View style={{ flex:1 }}>
                        <View style={{ justifyContent:'center', alignItems:'center' }}>
                            <Image
                                source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1717099686/Secure_lock_and_key_successfully_unlocked_it6hy0.png' }}
                                style={{ width: 250, height:250 }}
                                resizeMode='cover'
                            />
                        </View>
                        <View style={{ margin:15, borderRadius:8, padding:10 }}>
                            <Text size={18} theme='h1' textAlign='center'>WALLET SECURED!</Text>
                            <Text style={{ marginTop:5 }} size={14} theme='description' textAlign='center'>Select next to continue your wallet set up by adding a transaction account.</Text>
                        </View>
                    </View>
                )
            default: return <></>
        }
    }


    return (
        <View style={{flex:1}}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={setup_sections}
                    key='setup_sections_wallet'
                    keyExtractor={(item) => item}
                    renderItem={renderSections}
                />
            </View>
            <View style={{ flexDirection:'row', padding:20 }}>
                {!wallet_settings.agreed_to_terms && onClose ?
                <View style={{ flex:1, flexDirection:'row' }}>
                    <Button
                        title='EXIT SETUP'
                        style={{ flex:1, marginRight:5 }}
                        type='error'
                        onPress={() => onClose()}
                    />
                    <Button
                        title='I AGREE'
                        style={{ flex:3, opacity:terms_agreed?1:0.5 }}
                        disabled={!terms_agreed}
                        type='success'
                        onPress={() => handleAgreeTerms()}
                    />
                </View>
                : !wallet_settings.pin && pin_view == 'set' ?
                    <Button
                        title='CONFIRM PIN'
                        style={{ flex:3, opacity:pin_valid?1:0.5 }}
                        disabled={!pin_valid}
                        type='success'
                        onPress={() => setPinView('confirm')}
                    />
                : !wallet_settings.pin && pin_view == 'confirm' ?
                    <Button
                        title='LETS GO!'
                        style={{ flex:3, opacity:go_valid ? 1: 0.5}}
                        disabled={!go_valid}
                        type='success'
                        onPress={() => handleSetPin()}
                    />
                : auth_required ?
                <View style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                    {onClose ?
                    <Button
                        style={{ flex:1, marginRight:5 }}
                        title='CLOSE'
                        type='close'
                        onPress={() => onClose()}
                    />
                    :<></>}
                    <Button
                        title='SUBMIT'
                        style={{ flex:3, opacity:pin_valid ? 1: 0.5}}
                        disabled={!pin_valid}
                        type='success'
                        onPress={() => handleAuthPin()}
                    />
                </View>
                : onClose && onNext ?
                <View style={{ flex:1, flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
                    <Button
                        title='EXIT SETUP'
                        type='error'
                        onPress={() => onClose()}
                    />
                    <Button
                        float
                        style={{ borderRadius:100, height:50, width:50, justifyContent:'center', alignItems:'center' }}
                        onPress={() => onNext()}
                        //disabled={loading}
                        >
                        <Icons.ChevronIcon size={18} direction='right' color={Colors.text.h1}/>
                    </Button>
                </View>
                :<></>
                }
            </View>
        </View>
    )
}

export default WalletSetup